<template>
  <div>
    <illion-statement-uploader
      v-if="localStage === UPLOAD_STATEMENTS"
      :application="application"
      :updateApplicationWithStore="updateApplicationWithStore"
      @electronicUploadSuccessful="onElectronicUploadSuccessful"
    >
      <slot name="illion-statement-uploader-header" slot="illion-statement-uploader-header" />
      <slot
        name="illion-statement-uploader-online-tab-content"
        slot="illion-statement-uploader-online-tab-content"
      />
      <slot
        name="illion-statement-uploader-manual-tab-content"
        slot="illion-statement-uploader-manual-tab-content"
      />
      <slot name="illion-flow-consents" slot="illion-flow-consents" />
    </illion-statement-uploader>

    <adr-uploader v-else-if="showAdrBlock" @docUpload="onDocUpload">
      <slot name="adr-uploader-header" slot="adr-uploader-header" />
    </adr-uploader>

    <div v-if="showAdrBlock" class="errorMessage">
      <p class="errorText">{{ errorMessage }}</p>
    </div>

    <navigation-buttons @back="onBack" :next="true" :back="disableButtonBack">
      <brand-button :disabled="disableButtonNext" slot="next" @click="goNext">
        {{ $t('nextButtonLabel') }}
      </brand-button>
    </navigation-buttons>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import IllionStatementUploader from '@/components/illionComponents/IllionStatementUploader';
import AdrUploader from '@/components/illionComponents/AdrUploader';
import * as fileStates from '@/modules/upload-service/file-states';
import adrStatuses from '@/constants/adr-statuses';
import { ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR } from '@/modules/illion-adrs';
import { submitDocuments } from '@/modules/api-service/v3';

const UPLOAD_STATEMENTS = 'UPLOAD_STATEMENTS';
const UPLOAD_ADRS = 'UPLOAD_ADRS';

export default {
  name: 'illion-adr-uploader',
  components: {
    IllionStatementUploader,
    AdrUploader,
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
    updateApplicationWithStore: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      UPLOAD_STATEMENTS,
      UPLOAD_ADRS,
      localStage: UPLOAD_STATEMENTS,
      electronicUploadSuccessful: false,
      illionAdrs: [ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR],
      errorMessage: '',
    };
  },
  async created() {
    if (this.bankStatementAdr) {
      this.localStage = this.UPLOAD_STATEMENTS;
    } else {
      this.localStage = this.UPLOAD_ADRS;
      if (!this.filteredAdrs.length) {
        await this.goNext();
      }
    }
  },
  computed: {
    ...mapGetters({
      allFiles: 'file/getAll',
      allAdrs: 'adrs/getAdrs',
      isUploadInProgress: 'file/isUploadInProgress',
      filterFilesByQuery: 'file/filterFilesByQuery',
    }),
    filteredAdrs() {
      return this.allAdrs.filter(
        adr => adr.status === adrStatuses.REQUESTED && !this.illionAdrs.includes(adr.template.name),
      );
    },
    uploadedAdrs() {
      return this.filteredAdrs.filter(adr => {
        const uploadedFiles = this.filterFilesByQuery({ adrId: adr.id }).filter(
          file => file.isUploaded && !this.illionAdrs.includes(file.adr.template.name),
        );

        return uploadedFiles.length >= adr.numberOfFilesRequired;
      });
    },
    someFilesAreUploaded() {
      return !!this.uploadedAdrs.length;
    },
    uploadAdrsDisableCondition() {
      return !this.someFilesAreUploaded;
    },
    bankStatementAdr() {
      return this.allAdrs.find(
        adr => adr.status === adrStatuses.REQUESTED && this.illionAdrs.includes(adr.template.name),
      );
    },
    uploadedFiles() {
      return this.allFiles.filter(file => file.isUploaded);
    },
    bankStatementFilesUploaded() {
      return (
        this.bankStatementAdr &&
        this.uploadedFiles.find(file => file.adrId === this.bankStatementAdr.id)
      );
    },
    fileUploadServiceError() {
      return !!this.allFiles.filter(
        file =>
          file.documentUploadStatus === fileStates.ERROR ||
          file.documentUploadStatus === fileStates.DELETE_ERROR,
      ).length;
    },
    uploadStatementDisableCondition() {
      return (
        (!this.electronicUploadSuccessful && !this.bankStatementFilesUploaded) ||
        this.isUploadInProgress ||
        this.fileUploadServiceError
      );
    },
    disableButtonNext() {
      return this.localStage === this.UPLOAD_STATEMENTS
        ? this.uploadStatementDisableCondition
        : this.uploadAdrsDisableCondition;
    },
    disableButtonBack() {
      return this.localStage === this.UPLOAD_ADRS && this.bankStatementAdr
        ? {
            label: this.$t('backButtonLabel'),
            modifiers: {
              secondary: '',
            },
          }
        : false;
    },
    showAdrBlock() {
      return this.localStage === this.UPLOAD_ADRS;
    },
  },
  methods: {
    onElectronicUploadSuccessful() {
      this.electronicUploadSuccessful = true;
      this.$emit('electronicUploadSuccessful');
    },
    setErrorMessage(msg) {
      this.errorMessage = msg;
    },
    onDocUpload() {
      this.setErrorMessage('');
    },
    async goNext() {
      if (this.localStage === this.UPLOAD_STATEMENTS && this.filteredAdrs.length) {
        this.localStage = this.UPLOAD_ADRS;
      } else {
        if (this.uploadedAdrs.length !== this.filteredAdrs.length) {
          this.setErrorMessage(
            this.$t(
              'additionalDataUploader.electronicVerification.errorMessage.onlyPartiallyUploaded',
            ),
          );
          return;
        }
        await submitDocuments(this.application.id);
        await this.$store.dispatch('application/fetchApplication');
        this.$router.push({ name: this.$router.getRouteByStage(this.$stage.nextStage) });
      }
    },
    onBack() {
      if (this.localStage === this.UPLOAD_ADRS) {
        this.localStage = this.UPLOAD_STATEMENTS;
      } else {
        this.$router.push({ name: this.$router.getRouteByStage(this.$stage.previousStage) });
      }
    },
  },
};
</script>
<style lang="sass" scoped>
.errorMessage
  color: $color-system-validation-error
  +font-size(13.33,16)
  font-weight: normal
  margin-bottom: $glass-spacing-3
  .errorText
    margin-top: 0
</style>
